/**
 * API client with typed endpoints.
 * Integrates with the Darwin Resty backend.
 */

import axios, { AxiosInstance, AxiosProgressEvent } from 'axios';
import { appIdForLane, laneFromEnvironment } from '@darwin-launchpad/legacy-shared/telemetry';
import logger from './logger';
import { normalizeErrorDetail } from './apiErrors';
import type { IntegrationSlot, IntegrationHealth, EntityType, DetectionCategory, Case, CaseDocument, CaseStatus, FinalizeBatchResult, FinalizeMode, ReviewStatus, CaseSearchMode, CaseSearchResult, CaseSearchRedactRequest, CaseSearchRedactResult, CaseStatistics, DocumentProgress, DocumentStatus, ReviewDecision, RedactionReason, WordDictionaryEntry, EDiscoverySystem, EDiscoverySource, EDiscoverySearchScope, EDiscoveryCaseSummary, EDiscoveryCaseExports, RequestInfo, TopEntity, ContextAnalysis, CorrelationRisk, SteeringHint, RequestSearchResult, RequestServiceStatus, ExternalRequestDetail, ReviewMode, TableSummary, TableRowsResponse, TableRowsVersion, TableRulesResponse, TableRule, CreateTableRuleRequest, TableRuleDecision, TableRuleDecisionsResponse, TableRuleStatus, TableRowListResponse, TableNavDirection, TableCellDecisionInput, TableCellDecisionsResponse, TableCellListResponse } from '../types';
import type { PurviewQuerySuggestion } from './purviewQuery';
import type {
  AiTuning,
  DetectionSample,
  DetectionTryResult,
  PurviewFacts,
  PurviewTryResult,
} from './aiTuning';

// Types
export interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  status?: string;
  roles?: string[];
  tenant_id?: string;
  current_tenant?: {
    id: string;
    name: string;
    roles: string[];
  };
  created_at?: string;
  updated_at?: string;
  last_login?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

// Darwin format access token object
export interface AccessTokenObject {
  token: string;
  exp: string;
  roles?: string[];
  tenant_id?: string;
}

export interface LoginResponse {
  access_token: string | AccessTokenObject;
  refresh_token: string;
  user: User;
}

// Document types
export interface Document {
  id: string;
  name: string;
  type: string;
  upload_date: string;
  updated_at: string;
  uploaded_by: string;
  tenant_id: string;
  original_path: string;
  redacted_path?: string;
  file_size: number;
  page_count?: number;
  status: 'unprocessed' | 'needs_redaction' | 'redacted' | 'completed';
  metadata?: Record<string, unknown>;
  error_message?: string;
  case_id?: string;
  // Populated only when the case is fetched with include_progress=true.
  progress?: DocumentProgress;
}

// API returns the document directly with a message field, not wrapped
export interface DocumentUploadResponse extends Document {
  message: string;
}

export interface GetDocumentsParams {
  status?: string;
  limit?: number;
  offset?: number;
  search?: string;
  file_type?: string; // Comma-separated MIME types
}

export interface DocumentListResponse {
  documents: Document[];
  total: number;
  limit: number;
  offset: number;
}

export interface ChunkedUploadInitRequest {
  filename: string;
  total_size: number;
  file_type: string;
  total_chunks: number;
  metadata?: Record<string, unknown>;
  case_id?: string;
}

export interface ChunkedUploadInitResponse {
  upload_id: string;
  chunk_size: number;
  message: string;
}

export interface ChunkedUploadStatusResponse {
  upload_id: string;
  filename: string;
  total_chunks: number;
  chunks_received: number[];
  progress: number;
  completed: boolean;
}

export interface ChunkedUploadCompleteResponse {
  document: Document;
  message: string;
}

// Presigned direct-to-S3 upload types (browser uploads straight to object storage).
export interface PresignUploadRequest {
  filename: string;
  content_type?: string;
  size: number;
  case_id?: string;
}

export interface PresignPutResponse {
  method: string;
  url: string;
  key: string;
  headers: Record<string, string>;
}

export interface CreateMultipartResponse {
  key: string;
  uploadId: string;
}

export interface SignPartResponse {
  url: string;
}

export interface MultipartPartETag {
  PartNumber: number;
  ETag: string;
}

export interface CompleteMultipartResponse {
  location: string;
}

export interface FinalizeUploadRequest {
  key: string;
  filename: string;
  content_type?: string;
  size: number;
  case_id?: string;
  metadata?: Record<string, unknown>;
}

export interface PreviewUrlResponse {
  url: string;
  presigned: boolean;
  expires_in?: number;
}

// Unified HTML content types for all document types (DOCX, EML, MSG, TXT, etc.)
export interface HtmlContent {
  html: string;
  title: string | null;
  metadata: Record<string, unknown>;
  success: boolean;
  error_message?: string;
}

// Create axios instance
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7773';
const API_PREFIX = import.meta.env.VITE_API_PREFIX || 'launchpad/redactit/api';

export const api: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/${API_PREFIX}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'X-App-Id': appIdForLane('redactit', laneFromEnvironment(import.meta.env.VITE_ENVIRONMENT)),
  },
});

// API endpoints
export const authApi = {
  // Authentication
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post('/auth/login', data);
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  refreshToken: async (data: { access_token: string; refresh_token: string }): Promise<LoginResponse> => {
    const response = await api.post('/auth/refresh', data);
    return response.data;
  },

  getPermissions: async (): Promise<unknown> => {
    const response = await api.get('/auth/permissions');
    return response.data;
  },

  updateProfile: async (data: Partial<User>): Promise<User> => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },

  changePassword: async (data: { current_password: string; new_password: string }): Promise<void> => {
    await api.post('/auth/change-password', data);
  },
};

// Chunked upload tuning. Files larger than the threshold are uploaded in
// CHUNK_SIZE pieces (matching the server's ChunkedUploadService.CHUNK_SIZE) so a
// dropped connection only loses one chunk; smaller files use a single POST.
export const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
export const CHUNKED_UPLOAD_THRESHOLD = 10 * 1024 * 1024; // 10 MB

// GET /documents/supported-formats — built server-side from the registered file
// processors, so the upload dialogs list exactly what the pipeline handles.
export interface SupportedFormat {
  name: string;
  group: string;
  extensions: string[];
  mime_type: string;
  preview: string;
  detection: string;
  redacted_output: string;
  in_zip: boolean;
  in_email_attachments: boolean;
  limited: boolean;
  /** Reviewed and redacted as a PDF copy; the redacted download is a PDF. */
  converted_to_pdf?: boolean;
  note: string | null;
}

export interface UnsupportedFormat {
  name: string;
  extensions: string[];
  suggestion: string;
}

export interface SupportedFormatsResponse {
  summary: string;
  formats: SupportedFormat[];
  not_supported: UnsupportedFormat[];
}

export const documentsApi = {
  // Upload a single file (for files <= 100MB)
  uploadFile: async (
    file: File,
    options?: {
      metadata?: Record<string, unknown>;
      case_id?: string;
      onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
    }
  ): Promise<DocumentUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.metadata) {
      formData.append('metadata', JSON.stringify(options.metadata));
    }

    // Build URL with case_id query param if provided
    let url = '/documents/upload';
    if (options?.case_id) {
      url += `?case_id=${encodeURIComponent(options.case_id)}`;
    }

    const response = await api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options?.onUploadProgress,
      timeout: 0, // No timeout for file uploads - large files can take a long time
    });
    return response.data;
  },

  // Get all documents with optional filters
  getDocuments: async (params?: GetDocumentsParams): Promise<DocumentListResponse> => {
    const response = await api.get('/documents', { params });
    return response.data;
  },

  // Delete a document
  deleteDocument: async (documentId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/documents/${documentId}`);
    return response.data;
  },

  // Download a document
  downloadDocument: async (
    documentId: string,
    version: 'original' | 'redacted' = 'original',
    fallbackFilename?: string,
    format?: 'pdf'
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.get(`/documents/${documentId}/download`, {
      params: { version, ...(format ? { format } : {}) },
      responseType: 'arraybuffer',
      timeout: 0,
    });

    // Create blob from arraybuffer (avoids Chrome's XHR blob creation issue)
    const headerContentType = response.headers['content-type'];
    const contentType = typeof headerContentType === 'string' && headerContentType
      ? headerContentType
      : 'application/octet-stream';
    const blob = new Blob([response.data], { type: contentType });

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = fallbackFilename || 'download';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    return { blob, filename };
  },

  // Build the absolute download URL for a document. Used for streaming/range-based
  // PDF loading (PDF.js fetches this URL directly and pulls only the bytes it needs)
  // instead of downloading the whole file up front via downloadDocument().
  getDownloadUrl: (
    documentId: string,
    version: 'original' | 'redacted' = 'original',
    format?: 'pdf'
  ): string => {
    const params = new URLSearchParams({ version });
    if (format) params.set('format', format);
    return `${api.defaults.baseURL}/documents/${documentId}/download?${params.toString()}`;
  },

  // Get HTML content for any document type (DOCX, EML, MSG, TXT, etc.)
  getHtmlContent: async (documentId: string, version: 'original' | 'redacted' = 'original'): Promise<HtmlContent> => {
    const response = await api.get(`/documents/${documentId}/html-content`, { params: { version } });
    return response.data;
  },

  // Chunked upload - Initialize
  initChunkedUpload: async (data: ChunkedUploadInitRequest): Promise<ChunkedUploadInitResponse> => {
    const response = await api.post('/documents/upload/chunked/init', data);
    return response.data;
  },

  // Chunked upload - Upload a chunk (upload_id/chunk_number/checksum are query params)
  uploadChunk: async (
    uploadId: string,
    chunkNumber: number,
    chunkData: Blob,
    checksum?: string,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<{ upload_id: string; chunk_number: number; progress: number; message: string }> => {
    const formData = new FormData();
    formData.append('chunk', chunkData);

    const params = new URLSearchParams({
      upload_id: uploadId,
      chunk_number: String(chunkNumber),
    });
    if (checksum) {
      params.set('checksum', checksum);
    }

    const response = await api.post(`/documents/upload/chunked/chunk?${params.toString()}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
      timeout: 0, // No timeout for chunk uploads
    });
    return response.data;
  },

  // Chunked upload - Get status
  getChunkedUploadStatus: async (uploadId: string): Promise<ChunkedUploadStatusResponse> => {
    const response = await api.get(`/documents/upload/chunked/${uploadId}/status`);
    return response.data;
  },

  // Chunked upload - Complete
  completeChunkedUpload: async (uploadId: string): Promise<ChunkedUploadCompleteResponse> => {
    const response = await api.post(`/documents/upload/chunked/${uploadId}/complete`);
    return response.data;
  },

  // High-level chunked upload: init -> upload all chunks sequentially -> complete.
  // Returns the same shape as uploadFile() so callers can treat both uniformly.
  uploadFileChunked: async (
    file: File,
    options?: {
      metadata?: Record<string, unknown>;
      case_id?: string;
      onProgress?: (percent: number) => void;
    }
  ): Promise<DocumentUploadResponse> => {
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

    const { upload_id } = await documentsApi.initChunkedUpload({
      filename: file.name,
      total_size: file.size,
      file_type: file.type || 'application/octet-stream',
      total_chunks: totalChunks,
      metadata: options?.metadata,
      case_id: options?.case_id,
    });

    for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
      const start = chunkNumber * CHUNK_SIZE;
      const blob = file.slice(start, start + CHUNK_SIZE);
      await documentsApi.uploadChunk(upload_id, chunkNumber, blob);
      options?.onProgress?.(Math.round(((chunkNumber + 1) / totalChunks) * 100));
    }

    const { document, message } = await documentsApi.completeChunkedUpload(upload_id);
    return { ...document, message };
  },

  // ---- Presigned direct-to-S3 upload (browser uploads straight to storage) ----
  // These back the Uppy AwsS3 hooks (see lib/uppyUpload.ts). They go through the
  // authenticated axios `api` client; the actual S3 transfer is done by Uppy.

  presignSign: async (data: PresignUploadRequest): Promise<PresignPutResponse> => {
    const response = await api.post('/documents/upload/presign/sign', data);
    return response.data;
  },

  presignCreateMultipart: async (data: PresignUploadRequest): Promise<CreateMultipartResponse> => {
    const response = await api.post('/documents/upload/presign/create-multipart', data);
    return response.data;
  },

  presignSignPart: async (key: string, uploadId: string, partNumber: number): Promise<SignPartResponse> => {
    const response = await api.post('/documents/upload/presign/sign-part', { key, uploadId, partNumber });
    return response.data;
  },

  presignListParts: async (key: string, uploadId: string): Promise<MultipartPartETag[]> => {
    const response = await api.get('/documents/upload/presign/list-parts', { params: { key, uploadId } });
    return response.data;
  },

  presignCompleteMultipart: async (
    key: string,
    uploadId: string,
    parts: MultipartPartETag[]
  ): Promise<CompleteMultipartResponse> => {
    const response = await api.post('/documents/upload/presign/complete-multipart', { key, uploadId, parts });
    return response.data;
  },

  presignAbortMultipart: async (key: string, uploadId: string): Promise<void> => {
    await api.post('/documents/upload/presign/abort-multipart', { key, uploadId });
  },

  // Register an object the client already uploaded directly to storage. The backend
  // validates the stored object and creates the Document (+ extracts children).
  finalizeUpload: async (data: FinalizeUploadRequest): Promise<DocumentUploadResponse> => {
    const response = await api.post('/documents/finalize', data);
    return response.data;
  },

  // Resolve how to preview a document: a presigned S3 GET URL for native PDFs,
  // otherwise a flag telling the caller to use the backend /download route.
  getPreviewUrl: async (
    documentId: string,
    version: 'original' | 'redacted' = 'original'
  ): Promise<PreviewUrlResponse> => {
    const response = await api.get(`/documents/${documentId}/preview-url`, { params: { version } });
    return response.data;
  },

  // File formats the app supports and what it does with each
  getSupportedFormats: async (): Promise<SupportedFormatsResponse> => {
    const response = await api.get('/documents/supported-formats');
    return response.data;
  },

  // Resolve how to download a document as an attachment. When the file is stored
  // as-is on S3 this returns presigned=true with a direct presigned S3 URL the
  // browser can navigate to (a navigation isn't subject to CORS, unlike an XHR
  // following the /download 307 redirect cross-origin). Otherwise presigned=false
  // and the caller fetches the proxied /download route with its auth header.
  resolveDownloadUrl: async (
    documentId: string,
    version: 'original' | 'redacted' = 'original',
    format?: 'pdf'
  ): Promise<PreviewUrlResponse> => {
    const response = await api.get(`/documents/${documentId}/download-url`, {
      params: { version, ...(format ? { format } : {}) },
    });
    return response.data;
  },
};

// Health check endpoint
export const healthApi = {
  check: async (): Promise<{ status: string; timestamp: string }> => {
    const response = await axios.get(`${API_BASE_URL}/health`);
    return response.data;
  },
};

// Redaction types
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ManualRedaction {
  id: string;
  type: string;
  page: number;
  bounds: BoundingBox;
  color?: string;
  opacity?: number;
}

export interface ApplyRedactionsRequest {
  document_id: string;
  redactions: ManualRedaction[];
  create_new_version?: boolean;
}

export interface ApplyRedactionsResponse {
  document_id: string;
  redacted_document_id: string;
  redactions_applied: number;
  status: string;
  message: string;
}

export type RedactionMethod = 'blackout' | 'blur' | 'replace' | 'remove';

// Redaction job types
export type LlmProvider = "general" | "sensitive";

export interface RedactionJobRequest {
  document_id: string;
  entity_types?: EntityType[];
  redaction_method?: RedactionMethod;
  auto_approve?: boolean;
  custom_patterns?: string[];
  enable_ocr?: boolean;
  ocr_engine?: string;
  /**
   * Per-job LLM backend selector picked in the upload UI.
   * "general" routes to the Bedrock detector; "sensitive" routes to a local
   * Ollama model. Omit to let the server default decide.
   */
  llm_provider?: LlmProvider;
}

export interface RedactionJobResponse {
  job_id: string;
  document_id: string;
  status: string;
  created_at: string;
  entity_types: EntityType[];
  redaction_method: RedactionMethod;
  message: string;
}

// Container redaction response (for PST files that create jobs for all children)
export interface ContainerRedactionJobInfo {
  job_id: string;
  document_id: string;
  document_name: string;
  entities_detected: number;
}

export interface ContainerRedactionResponse {
  container_id: string;
  jobs_created: number;
  jobs: ContainerRedactionJobInfo[];
  message: string;
}

// Response from POST /redact/reprocess (per-document reprocess with manual-flag steering)
export type ReprocessMode = 'append' | 'replace';

export interface ReprocessResponse {
  job_id: string;
  document_id: string;
  status: string;
  mode: ReprocessMode;
  steering_count: number;
  preserved_count: number;
  message: string;
}

// Union type for createJob response
export type CreateJobResponse = RedactionJobResponse | ContainerRedactionResponse;

export interface RedactionJobStatus {
  job_id: string;
  document_id: string;
  status: string;
  progress: number;
  // Total pages in the source document (0 until known). Drives the per-page bar.
  total_pages?: number;
  // Best-effort seconds until the job finishes, recomputed server-side on each
  // per-page tick. null/undefined when no estimate is available yet.
  estimated_seconds_remaining?: number | null;
  entities_detected: number;
  entities_approved: number;
  entities_rejected: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  error_message?: string;
  // 'table' for a CSV reviewed in the table view (sc-11403): its findings live
  // in tablesApi, and /suggestions returns none. Absent on older servers.
  review_mode?: ReviewMode;
}

// Entity suggestion from AI detection
export interface EntitySuggestion {
  index: number;
  entity_id: string;
  entity_type: EntityType;
  text: string;
  confidence: number;
  page: number;
  bounds?: BoundingBox;
  replacement_text?: string;
  rationale?: string;
  status: 'pending' | 'approved' | 'rejected';
  source?: 'ai' | 'manual';
  dictionary_source?: 'global' | 'case' | 'file';
  dictionary_action?: 'always_redact' | 'always_allow';
  manually_overridden?: boolean;
  start_pos?: number;
  group_key?: string;
  group_label?: string;
  // DOB / BANKING / PHI / FERPA / PERSONNEL, or NONE (not suggested) for a DATE.
  date_context?: string | null;
  // Row optimistic-concurrency stamp. Echo it back on mutations
  // (entity_versions / expected_version) so a row that changed since this read
  // 409s instead of being silently overwritten.
  version?: number;
}

// Manual entity request/response types
export interface ManualEntityRequest {
  text: string;
  page: number;
  bounds?: BoundingBox;
  entity_type?: string;
  reason?: string;
}

export interface ManualEntityResponse {
  job_id: string;
  entity_id: string;
  entity_type: string;
  text: string;
  status: string;
  message: string;
}

export interface BlackoutPageResponse {
  action: 'created' | 'removed';
  entity_id: string;
  text: string;
  page: number;
}

export interface EntitySuggestionsResponse {
  job_id: string;
  document_id: string;
  suggestions: EntitySuggestion[];
  total: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  job_status: string;
  // Live preview: pages whose entities are already written and safe to render
  // while the job is still `processing`. Irrelevant once `job_status` reaches
  // `awaiting_approval` (everything is then final).
  ready_pages?: number[];
  total_pages?: number;
  // Delta polling: the job's entity-write cursor at this read. Pass it back as
  // `since` to receive only rows written after it. In a delta response
  // (`delta === true`) `suggestions` holds ONLY the changed rows and
  // `entity_ids` lists ALL currently visible ids (for delete reconciliation);
  // counts/total always cover the full visible set.
  update_seq?: number;
  delta?: boolean;
  entity_ids?: string[] | null;
}

export interface OcrWordBounds {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface OcrWord {
  text: string;
  page: number;
  bbox: OcrWordBounds;
}

export interface OcrWordsResponse {
  job_id: string;
  words: OcrWord[];
}

export interface TextInRegionResponse {
  job_id: string;
  page: number;
  text: string;
  word_count: number;
  // "pending" when the page isn't extracted yet; the lookup was queued and must be
  // polled via region-scan/{operation_id}.
  status?: 'done' | 'pending';
  operation_id?: string | null;
}

export interface RegionScanStatusResponse {
  operation_id: string;
  status: 'pending' | 'done' | 'failed';
  text: string;
  word_count: number;
}

export interface ApprovalRequest {
  approved_entities: string[];  // stable entity_ids
  rejected_entities?: string[];
  pending_entities?: string[];  // Reset to pending (de-redact)
  entity_reasons?: Record<string, string>;  // Map entity_id -> reason text
  // entity_id -> the row `version` we last read. Ids present here are written
  // compare-and-set server-side: a row that changed since that read is skipped
  // and reported in a 409 (the rest of the batch still applies).
  entity_versions?: Record<string, number>;
  apply_redactions?: boolean;
}

export interface ApprovalResponse {
  job_id: string;
  approved_count: number;
  rejected_count: number;
  status: string;
  message: string;
  redacted_document_id?: string;
}

export interface PendingDecisionResponse {
  job_id: string;
  updated_count: number;  // pending rows this request decided
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  status: string;  // the job's status after the decision
  update_seq: number;
}

export interface GroupDecisionRequest {
  // group_key values (from suggestions) to act on — membership is resolved
  // server-side at write time, not from this client's row snapshot.
  group_keys: string[];
  scope: 'file' | 'case' | 'global';
  reason?: string;  // approve only
}

export interface GroupDecisionResponse {
  job_id: string;
  updated_count: number;
  rule_words: string[];
  rules_changed: number;
  // 'enqueued': this job's targeted rescan was claimed + enqueued before the
  // response (file scope — an immediate progress fetch already sees it
  // `rescanning`). 'scheduled': the case/global fan-out runs in a background
  // task after the response, so jobs flip a beat later. 'none': no rule changed.
  rescan: 'enqueued' | 'scheduled' | 'none';
  update_seq: number;
}

export interface ApplyJobRedactionsResponse {
  job_id: string;
  document_id: string;
  redacted_document_id: string | null;
  redactions_applied: number;
  status: string;
  message: string;
}

// Feature flags
export interface FeatureFlags {
  enable_ocr: boolean;
  default_ocr_engine: string;
  available_ocr_engines: string[];
  category_learning: boolean;
}

export const settingsApi = {
  getFeatureFlags: async (): Promise<FeatureFlags> => {
    const response = await api.get('/settings/features');
    return response.data;
  },
};

// Redaction API endpoints
export const redactionApi = {
  // Create a new redaction job (triggers AI detection) - synchronous, blocks until detection completes
  // For container documents (PST), returns ContainerRedactionResponse with jobs for all children
  // For regular documents, returns RedactionJobResponse with a single job
  createJob: async (request: RedactionJobRequest): Promise<CreateJobResponse> => {
    const response = await api.post('/redact/', request, {
      timeout: 0, // No timeout - PST files process all children synchronously and can take a long time
    });
    return response.data;
  },

  // Create a new async redaction job (returns immediately, processes in background via RQ worker)
  // Same response types as createJob, but jobs start with status='queued'
  createJobAsync: async (request: RedactionJobRequest): Promise<CreateJobResponse> => {
    const response = await api.post('/redact/async', request);
    return response.data;
  },

  // Reprocess a single document, steered by its manually-flagged entities.
  // Queues an async job that injects the doc's source='manual' entities into
  // the LLM prompt ("find these and anything similar"). Returns immediately.
  // `mode` controls how prior results combine with the new run: 'append'
  // (default) keeps existing detections and adds new ones without duplication;
  // 'replace' drops the prior AI detections. Manual flags are preserved either way.
  reprocessDocument: async (
    documentId: string,
    mode: ReprocessMode = 'append',
  ): Promise<ReprocessResponse> => {
    const response = await api.post('/redact/reprocess', { document_id: documentId, mode });
    return response.data;
  },

  // Get redaction job status
  getJobStatus: async (jobId: string): Promise<RedactionJobStatus> => {
    const response = await api.get(`/redact/${jobId}`);
    return response.data;
  },

  // Get entity suggestions with bounding boxes for PDF display. Pass `since`
  // (a previous response's update_seq) to get a delta response instead of the
  // full list — see EntitySuggestionsResponse.
  getSuggestions: async (
    jobId: string,
    options: { includeRejected?: boolean; case_id?: string; since?: number } = {}
  ): Promise<EntitySuggestionsResponse> => {
    const response = await api.get(`/redact/${jobId}/suggestions`, {
      params: {
        include_rejected: options.includeRejected ?? false,
        ...(options.case_id ? { case_id: options.case_id } : {}),
        ...(options.since !== undefined ? { since: options.since } : {}),
      }
    });
    return response.data;
  },

  // Get OCR word positions for overlay display
  getOcrWords: async (jobId: string): Promise<OcrWordsResponse> => {
    const response = await api.get(`/redact/${jobId}/ocr-words`);
    return response.data;
  },

  // Extract the document text that falls inside a drawn region (for manual
  // redactions / reprocessing steering). If the page isn't extracted yet (document
  // still processing), the backend queues the lookup; we poll until it resolves so
  // callers always receive the recovered text.
  getTextInRegion: async (
    jobId: string,
    data: { page: number; bounds: BoundingBox }
  ): Promise<TextInRegionResponse> => {
    const response = await api.post(`/redact/${jobId}/text-in-region`, data);
    const result: TextInRegionResponse = response.data;
    if (result.status !== 'pending' || !result.operation_id) {
      return result;
    }

    // Poll the queued operation until the worker extracts the page (or we give up,
    // returning empty text so the manual redaction is still created).
    const operationId = result.operation_id;
    const intervalMs = 700;
    const maxAttempts = 90; // ~63s
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      try {
        const poll = await api.get<RegionScanStatusResponse>(
          `/redact/${jobId}/region-scan/${operationId}`
        );
        if (poll.data.status !== 'pending') {
          return {
            job_id: jobId,
            page: data.page,
            text: poll.data.text,
            word_count: poll.data.word_count,
            status: 'done',
          };
        }
      } catch (err) {
        logger.error('Region-scan poll failed', err);
        break;
      }
    }
    return { job_id: jobId, page: data.page, text: '', word_count: 0, status: 'done' };
  },

  // Approve or reject entity suggestions
  approveSuggestions: async (jobId: string, approval: ApprovalRequest): Promise<ApprovalResponse> => {
    const response = await api.put(`/redact/${jobId}/approve`, approval);
    return response.data;
  },

  // Scoped decision about whole entity GROUPS, addressed by group identity: the
  // server resolves membership at write time (covering occurrences added after
  // our last read), flips the rows, upserts one dictionary rule per distinct
  // member text under a SINGLE rules_version bump, and enqueues one targeted
  // rescan for the whole word set. Replaces the per-variant burst of
  // approve + dictionary-create pairs that raced each other and degraded every
  // multi-variant group action into a full-document rescan.
  approveGroups: async (jobId: string, decision: GroupDecisionRequest): Promise<GroupDecisionResponse> => {
    const response = await api.put(`/redact/${jobId}/groups/approve`, decision);
    return response.data;
  },

  // Mirror of approveGroups: reject the groups' occurrences and persist
  // always_allow rules.
  rejectGroups: async (jobId: string, decision: GroupDecisionRequest): Promise<GroupDecisionResponse> => {
    const response = await api.put(`/redact/${jobId}/groups/reject`, decision);
    return response.data;
  },

  // Decide every still-pending suggestion of the job in ONE request (earlier
  // per-row decisions stand; no dictionary rules are created).
  approveAllPending: async (jobId: string, reason?: string): Promise<PendingDecisionResponse> => {
    const response = await api.put(`/redact/${jobId}/pending/approve`, { reason });
    return response.data;
  },

  rejectAllPending: async (jobId: string): Promise<PendingDecisionResponse> => {
    const response = await api.put(`/redact/${jobId}/pending/reject`, {});
    return response.data;
  },

  // Cancel a redaction job
  cancelJob: async (jobId: string): Promise<void> => {
    await api.delete(`/redact/${jobId}`);
  },

  // Get all redaction jobs for the tenant
  getJobs: async (params?: {
    document_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<RedactionJobStatus[]> => {
    const response = await api.get('/redact/', { params });
    return response.data;
  },

  // Apply manual redactions to a document (PDF only, with coordinates)
  applyRedactions: async (request: ApplyRedactionsRequest): Promise<ApplyRedactionsResponse> => {
    const response = await api.post('/redact/apply', request);
    return response.data;
  },

  // Apply approved redactions from a job to create a redacted document
  // Works for all file types - auto-detects method based on document type.
  // When asyncApply is true the CPU-heavy burn runs on the Kafka worker off the
  // request path (B1 of sc-6853): the response carries status 'redacting' and the
  // caller polls getJobStatus until the job is 'completed'/'failed'.
  applyJobRedactions: async (
    jobId: string,
    asyncApply = false
  ): Promise<ApplyJobRedactionsResponse> => {
    const response = await api.post(`/redact/${jobId}/apply`, null, {
      params: asyncApply ? { async_apply: true } : undefined,
    });
    return response.data;
  },

  // Add a manual redaction entity to an existing job
  addManualEntity: async (jobId: string, data: ManualEntityRequest): Promise<ManualEntityResponse> => {
    const response = await api.post(`/redact/${jobId}/manual`, data);
    return response.data;
  },

  // Delete a manual redaction entity from an existing job. `expectedVersion`
  // (the row version we last read) makes the delete compare-and-set: 409 if the
  // entity changed since.
  deleteManualEntity: async (jobId: string, entityId: string, expectedVersion?: number): Promise<void> => {
    await api.delete(`/redact/${jobId}/manual/${entityId}`, {
      params: expectedVersion !== undefined ? { expected_version: expectedVersion } : undefined,
    });
  },

  // Change the entity type of an existing detected entity (AI or manual).
  // `expectedVersion` makes the update compare-and-set (409 on a stale read).
  updateEntityType: async (
    jobId: string,
    entityId: string,
    entityType: string,
    expectedVersion?: number,
  ): Promise<{ job_id: string; entity_id: string; entity_type: string; message: string }> => {
    const response = await api.patch(`/redact/${jobId}/entity/${entityId}/type`, {
      entity_type: entityType,
      ...(expectedVersion !== undefined ? { expected_version: expectedVersion } : {}),
    });
    return response.data;
  },

  // Retype several entities at once (a review card's occurrences, FB-1012). Rows
  // in entityVersions are compare-and-set: changed rows are skipped with a 409.
  // learn records the correction on the tenant's categories.
  updateEntitiesType: async (
    jobId: string,
    entityIds: string[],
    entityType: string,
    entityVersions: Record<string, number> = {},
    learn = true,
  ): Promise<UpdateEntitiesTypeResponse> => {
    const response = await api.patch(`/redact/${jobId}/entities/type`, {
      entity_ids: entityIds,
      entity_type: entityType,
      entity_versions: entityVersions,
      learn,
    });
    return response.data;
  },

  // Toggle page blackout (create or remove)
  blackoutPage: async (jobId: string, data: { page: number; reason?: string }): Promise<BlackoutPageResponse> => {
    const response = await api.post(`/redact/${jobId}/blackout-page`, data);
    return response.data;
  },
};

// Case types
export interface CreateCaseRequest {
  name: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateCaseRequest {
  name?: string;
  notes?: string;
}

export interface CaseListResponse {
  cases: Case[];
  total: number;
  limit: number;
  offset: number;
}

export interface GetCasesParams {
  status?: CaseStatus;
  owner?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CaseWithDocumentsResponse extends Case {
  documents: Document[];
}

export interface AddDocumentsToCaseRequest {
  document_ids: string[];
}

export interface UpdateDocumentStatusRequest {
  status: DocumentStatus;
}

// Envelope returned by the case export endpoints when async_export builds the
// archive on the Kafka worker (B5/B6 of sc-6853): the client polls for the result.
export interface CaseExportJobResponse {
  export_id: string;
  status: string;
  // Present on the poll's `200 ready` response: a presigned S3 GET URL the client
  // navigates to, plus the download filename.
  url?: string;
  filename?: string;
}

// A worker-built export resolves to either a presigned URL the browser navigates
// to (S3 path) or an in-memory blob (synchronous proxied fallback). Exactly one of
// `url`/`blob` is set.
export type DecisionLogFormat = 'csv' | 'pdf';

export type WorkerExportResult = { filename: string; url?: string; blob?: Blob };

function filenameFromContentDisposition(
  contentDisposition: string | undefined,
  fallback: string
): string {
  if (!contentDisposition) return fallback;
  const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
  return match?.[1] || fallback;
}

// Build a case export off the API request path: ask the server to build it on the
// Kafka worker (async_export=true), then poll the per-export_id endpoint until the
// worker stores the result in S3 and the poll returns `200 ready` with a presigned
// GET url — which the caller navigates to, so the archive bytes flow straight from
// storage and never transit the API server. (A JSON url rather than a 307 redirect
// keeps us off an XHR-followed cross-origin redirect, which S3 blocks via CORS.)
// Falls back transparently to the synchronous proxied stream when the backend cannot
// presign (the enqueue returns the bytes directly as a blob).
async function fetchWorkerBuiltExport(
  enqueuePath: string,
  enqueueParams: Record<string, unknown>,
  pollPath: (exportId: string) => string,
  defaults: { filename: string; contentType: string }
): Promise<WorkerExportResult> {
  const enqueue = await api.get(enqueuePath, {
    params: { ...enqueueParams, async_export: true },
    responseType: 'arraybuffer',
    timeout: 0,
  });

  const enqueueType = (enqueue.headers['content-type'] as string) || '';
  // Non-JSON => the backend can't presign, so it streamed the archive synchronously.
  if (!enqueueType.includes('application/json')) {
    const contentType = enqueueType || defaults.contentType;
    return {
      blob: new Blob([enqueue.data], { type: contentType }),
      filename: filenameFromContentDisposition(
        enqueue.headers['content-disposition'],
        defaults.filename
      ),
    };
  }

  const envelope = JSON.parse(new TextDecoder().decode(enqueue.data)) as CaseExportJobResponse;
  if (!envelope.export_id) {
    throw new Error('Export was not enqueued by the server');
  }

  const POLL_INTERVAL_MS = 1500;
  const MAX_POLLS = 800; // ~20 min ceiling, well under the worker's own timeout
  for (let i = 0; i < MAX_POLLS; i++) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    const res = await api.get(pollPath(envelope.export_id), {
      responseType: 'arraybuffer',
      timeout: 0,
      // 202 = still building, 200 = a JSON envelope with the presigned GET url,
      // 502 = the worker wrote a terminal failure marker. Anything else throws.
      validateStatus: (s) => s === 200 || s === 202 || s === 502,
    });

    if (res.status === 202) continue;

    if (res.status === 502) {
      let detail = 'Export build failed on the worker';
      try {
        detail = JSON.parse(new TextDecoder().decode(res.data)).detail || detail;
      } catch {
        // keep the default message if the failure body isn't JSON
      }
      throw new Error(detail);
    }

    // 200: the build is done; the envelope carries a presigned GET url the caller
    // navigates to (S3 sets the attachment filename, so it survives the hop).
    const ready = JSON.parse(new TextDecoder().decode(res.data)) as CaseExportJobResponse;
    if (!ready.url) {
      throw new Error('Export finished but no download URL was returned');
    }
    return { url: ready.url, filename: ready.filename || defaults.filename };
  }

  throw new Error('Export is taking longer than expected. Please try again.');
}

// Cases API endpoints
export const casesApi = {
  // Create a new case
  createCase: async (data: CreateCaseRequest): Promise<Case> => {
    const response = await api.post('/cases/', data);
    return response.data;
  },

  // Get all cases with optional filters
  getCases: async (params?: GetCasesParams): Promise<CaseListResponse> => {
    const response = await api.get('/cases/', { params });
    return response.data;
  },

  // Get a specific case by ID with its documents. Pass includeProgress to embed
  // per-document redaction progress (sidebar bars) and avoid a separate job-list fetch.
  getCase: async (
    caseId: string,
    options?: { includeProgress?: boolean }
  ): Promise<CaseWithDocumentsResponse> => {
    const response = await api.get(`/cases/${caseId}`, {
      params: options?.includeProgress ? { include_progress: true } : undefined,
    });
    return response.data;
  },

  // Update a case
  updateCase: async (caseId: string, data: UpdateCaseRequest): Promise<Case> => {
    const response = await api.put(`/cases/${caseId}`, data);
    return response.data;
  },

  // Delete a case (soft delete)
  deleteCase: async (caseId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/cases/${caseId}`);
    return response.data;
  },

  // Get deleted cases (trash)
  getDeletedCases: async (params?: { limit?: number; offset?: number }): Promise<CaseListResponse> => {
    const response = await api.get('/cases/', { params: { ...params, deleted: true } });
    return response.data;
  },

  // Restore a soft-deleted case
  restoreCase: async (caseId: string): Promise<Case> => {
    const response = await api.post(`/cases/${caseId}/restore`);
    return response.data;
  },

  // Permanently delete a case
  permanentDeleteCase: async (caseId: string): Promise<void> => {
    await api.delete(`/cases/${caseId}/permanent`);
  },

  // Add documents to a case
  addDocuments: async (caseId: string, documentIds: string[]): Promise<{ message: string; added_count: number }> => {
    const response = await api.post(`/cases/${caseId}/documents`, { document_ids: documentIds });
    return response.data;
  },

  // Remove a document from a case
  removeDocument: async (caseId: string, documentId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/cases/${caseId}/documents/${documentId}`);
    return response.data;
  },

  // Update a document's status within a case
  updateDocumentStatus: async (
    caseId: string,
    documentId: string,
    status: DocumentStatus
  ): Promise<Document> => {
    const response = await api.put(`/cases/${caseId}/documents/${documentId}/status`, { status });
    return response.data;
  },

  // Set or clear (null) a document's review decision — responsive / non-responsive / needs review.
  // `thread_updates`: replies that followed through the email thread (sc-11471).
  setReviewDecision: async (
    caseId: string,
    documentId: string,
    decision: ReviewDecision | null
  ): Promise<CaseDocument & { thread_updates?: CaseDocument[] }> => {
    const response = await api.put(`/cases/${caseId}/documents/${documentId}/review-decision`, { decision });
    return response.data;
  },

  // Set one review decision on several documents. Ids outside the case and
  // containers are returned in `skipped`.
  bulkSetReviewDecision: async (
    caseId: string,
    documentIds: string[],
    decision: ReviewDecision | null
  ): Promise<{ documents: CaseDocument[]; skipped: string[]; thread_updates?: CaseDocument[] }> => {
    const response = await api.put(`/cases/${caseId}/documents/review-decision`, {
      document_ids: documentIds,
      decision,
    });
    return response.data;
  },

  // Undo thread tagging (sc-11471): clear the replies tagged Responsive from this email.
  undoThreadDecisions: async (caseId: string, documentId: string): Promise<{ documents: CaseDocument[] }> => {
    const response = await api.delete(`/cases/${caseId}/documents/${documentId}/thread-decisions`);
    return response.data;
  },

  // Review panel (FR-14): per-document finalization state for the Finish tab.
  getReviewStatus: async (caseId: string): Promise<ReviewStatus> => {
    const response = await api.get(`/cases/${caseId}/review-status`);
    return response.data;
  },

  // Queue Finalize / Re-finalize on the worker; documents that need a reviewer
  // come back in `needs_review` with the reason.
  finalizeBatch: async (caseId: string, documentIds: string[], mode: FinalizeMode): Promise<FinalizeBatchResult> => {
    const response = await api.post(`/cases/${caseId}/finalize-batch`, { document_ids: documentIds, mode });
    return response.data;
  },

  // Case search (FR-04): find a term in every document of the case.
  searchCase: async (caseId: string, query: string, mode: CaseSearchMode, signal?: AbortSignal): Promise<CaseSearchResult> => {
    const response = await api.get(`/cases/${caseId}/search`, { params: { q: query, mode }, signal });
    return response.data;
  },

  // Redact the selected (or all) case-search hits as approved suggestions.
  redactSearchHits: async (caseId: string, body: CaseSearchRedactRequest): Promise<CaseSearchRedactResult> => {
    const response = await api.post(`/cases/${caseId}/search/redact`, body);
    return response.data;
  },

  // Finalize a document (mark as completed)
  finalizeDocument: async (caseId: string, documentId: string): Promise<Document> => {
    const response = await api.post(`/cases/${caseId}/documents/${documentId}/finalize`);
    return response.data;
  },

  // Get case statistics
  getCaseStatistics: async (caseId: string): Promise<CaseStatistics> => {
    const response = await api.get(`/cases/${caseId}/statistics`);
    return response.data;
  },

  // Download the case's decision log (review decisions with how they were made,
  // searches, redaction approvals and finalizations) as CSV or PDF (R2-11).
  // The file name is built by the caller (decisionLogFilename): Content-Disposition
  // isn't readable cross-origin.
  downloadDecisionLog: async (caseId: string, format: DecisionLogFormat): Promise<{ blob: Blob }> => {
    const response = await api.get(`/cases/${caseId}/decision-log`, {
      params: { format },
      responseType: 'arraybuffer',
      timeout: 0,
    });
    const contentType = (response.headers['content-type'] as string) || (format === 'pdf' ? 'application/pdf' : 'text/csv');
    return { blob: new Blob([response.data], { type: contentType }) };
  },

  // Download all case documents as a ZIP archive. Built on the Kafka worker and
  // served via a presigned S3 GET (B5 of sc-6853) so the archive bytes never transit
  // the API server; falls back to a synchronous proxied stream on non-S3 backends.
  // `filter` narrows the documents (`decisions`, `finalized_only`; see exportQueryParams).
  downloadCaseAsZip: async (
    caseId: string,
    version: 'original' | 'redacted' = 'redacted',
    filter: Record<string, string | boolean> = {}
  ): Promise<WorkerExportResult> => {
    return fetchWorkerBuiltExport(
      `/cases/${caseId}/download`,
      { version, ...filter },
      (exportId) => `/cases/${caseId}/exports/${exportId}`,
      { filename: 'case.zip', contentType: 'application/zip' }
    );
  },

  // Export all case documents as a single merged PDF (mega-PDF pipeline). Built on
  // the Kafka worker and served via a presigned S3 GET (B6 of sc-6853) so the output
  // bytes never transit the API server; falls back to a synchronous proxied stream on
  // non-S3 backends. The result is a single .pdf or a .zip of volumes — the default
  // filename adapts to the returned content-type.
  exportCaseAsMegaPdf: async (
    caseId: string,
    options: {
      version?: 'original' | 'redacted';
      pdfStandard?: 'pdf' | 'pdfa';
      splitThreshold?: number;
      includeSeparators?: boolean;
      filter?: Record<string, string | boolean>;
    } = {}
  ): Promise<WorkerExportResult> => {
    const {
      version = 'redacted',
      pdfStandard = 'pdf',
      splitThreshold = 2000,
      includeSeparators = true,
      filter = {},
    } = options;
    const result = await fetchWorkerBuiltExport(
      `/cases/${caseId}/export-mega-pdf`,
      {
        version,
        pdf_standard: pdfStandard,
        split_threshold: splitThreshold,
        include_separators: includeSeparators,
        ...filter,
      },
      (exportId) => `/cases/${caseId}/mega-pdf-exports/${exportId}`,
      { filename: 'case.pdf', contentType: 'application/pdf' }
    );
    // A multi-volume export comes back as a ZIP; correct the default .pdf name.
    // Only needed on the proxied blob fallback — the presigned path's filename is
    // already correct (the server derives .pdf/.zip from the stored result).
    if (result.filename === 'case.pdf' && result.blob?.type.includes('zip')) {
      return { ...result, filename: 'case.zip' };
    }
    return result;
  },
};

// Reason types
export interface ReasonLegalFields {
  citation?: string;
  legal_text?: string;
  url?: string;
}

export interface CreateReasonRequest extends ReasonLegalFields {
  title: string;
  description?: string;
}

export interface UpdateReasonRequest extends ReasonLegalFields {
  title?: string;
  description?: string;
}

export interface ReasonListResponse {
  reasons: RedactionReason[];
  total: number;
  limit: number;
  offset: number;
}

export interface GetReasonsParams {
  limit?: number;
  offset?: number;
}

// Reasons API endpoints
// Detection categories (sc-11031): the tenant's editable entity types.
export interface CategoryInput {
  label: string;
  description?: string;
  color?: string;
  enabled?: boolean;
  patterns?: string[];
  examples?: string[];
  counter_examples?: string[];
}

/** A drafted category from a finding (FB-1011): POST /categories/draft. */
export interface CategoryDraftInput {
  label: string;
  values: string[];
  context?: string[];
  job_id?: string;
}

export interface CategoryDraft {
  description: string;
  patterns: string[];
  color: string;
  /** 'heuristic' when the AI was unavailable and the draft came from the value's format. */
  source: 'llm' | 'heuristic';
}

export interface UpdateEntitiesTypeResponse {
  job_id: string;
  entity_type: string;
  updated: string[];
  learned: { examples_added_to: string | null; counter_examples_added_to: string | null; values: string[] } | null;
}

// CSV table mode (sc-11403): the /tables API of docs/csv-table-mode.md.
const tablePath = (jobId: string) => `/tables/${encodeURIComponent(jobId)}`;

export const tablesApi = {
  // T1: phase, progress, columns, counts and the rules_seq change cursor.
  getSummary: async (jobId: string): Promise<TableSummary> => {
    const response = await api.get(tablePath(jobId));
    return response.data;
  },

  // T2: a contiguous row range (limit 1–500) with its hits, cell decisions and
  // effective redactions. `redacted` serves the finalized file's cells only.
  getRows: async (
    jobId: string,
    params: { start: number; limit: number; version?: TableRowsVersion },
  ): Promise<TableRowsResponse> => {
    const response = await api.get(`${tablePath(jobId)}/rows`, {
      params: { start: params.start, limit: params.limit, version: params.version ?? 'original' },
    });
    return response.data;
  },

  // T3
  getRules: async (jobId: string): Promise<TableRulesResponse> => {
    const response = await api.get(`${tablePath(jobId)}/rules`);
    return response.data;
  },

  // T4: "Redact this column…" — a reviewer's whole-column rule (created approved).
  createRule: async (jobId: string, body: CreateTableRuleRequest): Promise<TableRule> => {
    const response = await api.post(`${tablePath(jobId)}/rules`, body);
    return response.data;
  },

  // T6: accept / reject / undo (status "pending") rules, CAS on expected_version.
  decideRules: async (jobId: string, decisions: TableRuleDecision[]): Promise<TableRuleDecisionsResponse> => {
    const response = await api.put(`${tablePath(jobId)}/rules/decisions`, { decisions });
    return response.data;
  },

  // T7: decide every pending rule at once.
  decidePendingRules: async (
    jobId: string,
    status: Exclude<TableRuleStatus, 'pending'>,
    reason: string | null = null,
  ): Promise<TableRuleDecisionsResponse> => {
    const response = await api.put(`${tablePath(jobId)}/rules/pending`, { status, reason });
    return response.data;
  },

  // T8: only manual rules can be deleted (others: reject).
  deleteRule: async (jobId: string, ruleId: string): Promise<void> => {
    await api.delete(`${tablePath(jobId)}/rules/${encodeURIComponent(ruleId)}`);
  },

  // T13: change a rule's category.
  setRuleType: async (
    jobId: string,
    ruleId: string,
    entityType: EntityType,
    expectedVersion: number | null,
  ): Promise<TableRuleDecisionsResponse> => {
    const response = await api.patch(`${tablePath(jobId)}/rules/${encodeURIComponent(ruleId)}/type`, {
      entity_type: entityType,
      expected_version: expectedVersion,
    });
    return response.data;
  },

  // T9: rows holding a hit of one rule, after `after` in `direction` (a page of a rule group).
  ruleRows: async (
    jobId: string,
    ruleId: string,
    params: { after: number; limit: number; direction: TableNavDirection },
  ): Promise<TableRowListResponse> => {
    const response = await api.get(`${tablePath(jobId)}/rules/${encodeURIComponent(ruleId)}/rows`, { params });
    return response.data;
  },

  // T11: cell overrides, at most 5,000 per request.
  decideCells: async (jobId: string, decisions: TableCellDecisionInput[]): Promise<TableCellDecisionsResponse> => {
    const response = await api.put(`${tablePath(jobId)}/cells`, { decisions });
    return response.data;
  },

  // T14: "First row is a header" — stored, then detection runs again (a new job).
  setHeader: async (jobId: string, hasHeader: boolean): Promise<{ job_id: string; has_header: boolean }> => {
    const response = await api.put(`${tablePath(jobId)}/header`, { has_header: hasHeader });
    return response.data;
  },

  // T12: the reviewer's cell decisions, by row.
  listCells: async (
    jobId: string,
    params: { kind: 'manual' | 'all'; after_row: number; limit: number },
  ): Promise<TableCellListResponse> => {
    const response = await api.get(`${tablePath(jobId)}/cells`, { params });
    return response.data;
  },
};

export const categoriesApi = {
  list: async (): Promise<DetectionCategory[]> => {
    const response = await api.get('/categories/');
    return response.data.categories;
  },

  create: async (data: CategoryInput): Promise<DetectionCategory> => {
    const response = await api.post('/categories/', data);
    return response.data;
  },

  update: async (key: string, data: Partial<CategoryInput>): Promise<DetectionCategory> => {
    const response = await api.put(`/categories/${encodeURIComponent(key)}`, data);
    return response.data;
  },

  remove: async (key: string): Promise<void> => {
    await api.delete(`/categories/${encodeURIComponent(key)}`);
  },

  // Full reset: the whole list goes back to the defaults.
  reset: async (): Promise<DetectionCategory[]> => {
    const response = await api.post('/categories/reset');
    return response.data.categories;
  },

  // Draft a new category from a finding (FB-1011); nothing is saved.
  draft: async (data: CategoryDraftInput): Promise<CategoryDraft> => {
    const response = await api.post('/categories/draft', data);
    return response.data;
  },
};

// AI Tuning (sc-11888): the agency's instructions for the AI, one live version per task.
export const aiTuningApi = {
  get: async (): Promise<AiTuning> => {
    const response = await api.get('/ai-tuning/');
    return response.data;
  },

  saveDetection: async (instructions: string): Promise<AiTuning> => {
    const response = await api.put('/ai-tuning/detection', { instructions });
    return response.data;
  },

  savePurview: async (instructions: string, facts: PurviewFacts): Promise<AiTuning> => {
    const response = await api.put('/ai-tuning/purview', { instructions, facts });
    return response.data;
  },

  detectionSamples: async (): Promise<DetectionSample[]> => {
    const response = await api.get('/ai-tuning/detection/samples');
    return response.data.samples;
  },

  // Live vs edited instructions on one page of a processed document; saves nothing.
  tryDetection: async (instructions: string, jobId: string, page: number): Promise<DetectionTryResult> => {
    const response = await api.post(
      '/ai-tuning/detection/try',
      { instructions, job_id: jobId, page },
      { timeout: 180_000 },
    );
    return response.data;
  },

  // Live vs edited Purview instructions/facts on a request; saves nothing.
  tryPurview: async (instructions: string, facts: PurviewFacts, requestText: string): Promise<PurviewTryResult> => {
    const response = await api.post(
      '/ai-tuning/purview/try',
      { instructions, facts, request_text: requestText },
      { timeout: 180_000 },
    );
    return response.data;
  },
};

export const reasonsApi = {
  // Create a new reason
  createReason: async (data: CreateReasonRequest): Promise<RedactionReason> => {
    const response = await api.post('/reasons/', data);
    return response.data;
  },

  // Get all reasons for the tenant
  getReasons: async (params?: GetReasonsParams): Promise<ReasonListResponse> => {
    const response = await api.get('/reasons/', { params });
    return response.data;
  },

  // Get a specific reason by ID
  getReason: async (reasonId: string): Promise<RedactionReason> => {
    const response = await api.get(`/reasons/${reasonId}`);
    return response.data;
  },

  // Update a reason
  updateReason: async (reasonId: string, data: UpdateReasonRequest): Promise<RedactionReason> => {
    const response = await api.put(`/reasons/${reasonId}`, data);
    return response.data;
  },

  // Delete a reason (soft delete)
  deleteReason: async (reasonId: string): Promise<void> => {
    await api.delete(`/reasons/${reasonId}`);
  },
};

// Dictionary API types
export interface DictionaryEntryCreateRequest {
  scope_type: 'global' | 'case' | 'file';
  scope_id?: string;
  word: string;
  match_type?: 'exact' | 'contains';
  action: 'always_redact' | 'always_allow';
  reason?: string;
  // Fallback entity type for occurrences the detector missed (backend-validated).
  entity_type?: string;
}

// Response to createEntry — just the persisted dictionary entry. The rescan it
// triggers now always runs on the worker (file scope included), so no post-rescan
// suggestions come back here; the client reconciles by polling the affected job
// until the worker flips it back to awaiting_approval.
export type DictionaryEntryCreateResponse = WordDictionaryEntry;

export interface DictionaryEntryUpdateRequest {
  word?: string;
  match_type?: 'exact' | 'contains';
  action?: 'always_redact' | 'always_allow';
  reason?: string;
}

export interface DictionaryEntryListResponse {
  entries: WordDictionaryEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface GetDictionaryEntriesParams {
  scope_type?: 'global' | 'case' | 'file';
  scope_id?: string;
  limit?: number;
  offset?: number;
}

// Map of dictionary entry id -> occurrence count across the scope's jobs.
export interface DictionaryOccurrenceCountsResponse {
  counts: Record<string, number>;
}

export interface GetOccurrenceCountsParams {
  scope_type: 'global' | 'case' | 'file';
  scope_id?: string;
}

// List import / export (sc-11468). `action` picks the list; the UI ships on the Allow List.
export type DictionaryAction = 'always_redact' | 'always_allow';
export type ImportLayout = 'rows' | 'columns_per_category';
export type ImportMatch = 'exact' | 'contains';
export type ImportRowStatus = 'new' | 'update' | 'unchanged' | 'duplicate' | 'conflict' | 'error';

export interface ImportColumnMapping {
  layout: ImportLayout;
  has_header: boolean;
  phrase_column: number | null;
  category_column: number | null;
  match_column: number | null;
  category_columns: number[];
  match_by_category: Record<string, ImportMatch>;
  default_match: ImportMatch;
}

export interface ImportColumn {
  index: number;
  label: string;
  header: string | null;
  samples: string[];
}

export interface ImportPreviewRow {
  line: number;
  column: string | null;
  phrase: string;
  category: string | null;
  match: ImportMatch;
  status: ImportRowStatus;
  message: string | null;
}

export interface ImportCategorySummary {
  category: string | null;
  match: ImportMatch;
  total: number;
  new: number;
  update: number;
  unchanged: number;
  duplicate: number;
  conflict: number;
  error: number;
}

export interface ImportPreviewTotals {
  phrases: number;
  new: number;
  update: number;
  unchanged: number;
  duplicate: number;
  conflict: number;
  error: number;
  /** What "Replace list" would remove (Add removes nothing). */
  remove: number;
  existing: number;
}

export interface ImportPreviewResponse {
  file_name: string | null;
  action: DictionaryAction;
  mapping: ImportColumnMapping;
  mapping_source: 'llm' | 'heuristic' | 'user';
  mapping_problems: string[];
  columns: ImportColumn[];
  totals: Partial<ImportPreviewTotals>;
  categories: ImportCategorySummary[];
  rows: ImportPreviewRow[];
  issues: ImportPreviewRow[];
  rows_truncated: boolean;
  issues_truncated: boolean;
}

export interface ImportResultResponse {
  action: DictionaryAction;
  mode: 'add' | 'replace';
  added: number;
  updated: number;
  removed: number;
  unchanged: number;
  skipped: number;
  rescan_scheduled: boolean;
}

const importForm = (file: File, action: DictionaryAction, mapping?: ImportColumnMapping) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('action', action);
  if (mapping) formData.append('mapping', JSON.stringify(mapping));
  return formData;
};

// Dictionary API endpoints
export const dictionaryApi = {
  getEntries: async (params?: GetDictionaryEntriesParams): Promise<DictionaryEntryListResponse> => {
    const response = await api.get('/dictionaries/', { params });
    return response.data;
  },

  createEntry: async (data: DictionaryEntryCreateRequest): Promise<DictionaryEntryCreateResponse> => {
    const response = await api.post('/dictionaries/', data);
    return response.data;
  },

  updateEntry: async (id: string, data: DictionaryEntryUpdateRequest): Promise<WordDictionaryEntry> => {
    const response = await api.put(`/dictionaries/${id}`, data);
    return response.data;
  },

  deleteEntry: async (id: string): Promise<void> => {
    await api.delete(`/dictionaries/${id}`);
  },

  getGlobal: async (): Promise<DictionaryEntryListResponse> => {
    const response = await api.get('/dictionaries/global');
    return response.data;
  },

  getForCase: async (caseId: string): Promise<DictionaryEntryListResponse> => {
    const response = await api.get(`/dictionaries/case/${caseId}`);
    return response.data;
  },

  getForFile: async (documentId: string): Promise<DictionaryEntryListResponse> => {
    const response = await api.get(`/dictionaries/file/${documentId}`);
    return response.data;
  },

  // Per-entry occurrence counts (blast radius) for a scope's dictionary entries.
  getOccurrenceCounts: async (
    params: GetOccurrenceCountsParams
  ): Promise<DictionaryOccurrenceCountsResponse> => {
    const response = await api.get('/dictionaries/occurrences', { params });
    return response.data;
  },

  // The tenant-wide list as CSV (phrase, match, category).
  exportList: async (action: DictionaryAction): Promise<Blob> => {
    const response = await api.get('/dictionaries/export', { params: { action }, responseType: 'blob' });
    return response.data;
  },

  // The import template: the export's headers plus a few fictional rows.
  getImportTemplate: async (action: DictionaryAction): Promise<Blob> => {
    const response = await api.get('/dictionaries/import/template', {
      params: { action },
      responseType: 'blob',
    });
    return response.data;
  },

  // Parse an upload and show what an import would do. Without `mapping` the
  // server proposes one (internal LLM on the header + first rows, else heuristics).
  previewImport: async (
    file: File,
    action: DictionaryAction,
    mapping?: ImportColumnMapping
  ): Promise<ImportPreviewResponse> => {
    const response = await api.post('/dictionaries/import/preview', importForm(file, action, mapping), {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    });
    return response.data;
  },

  // Apply the import: add to the list, or replace it. One rescan follows.
  runImport: async (
    file: File,
    action: DictionaryAction,
    mapping: ImportColumnMapping,
    mode: 'add' | 'replace'
  ): Promise<ImportResultResponse> => {
    const formData = importForm(file, action, mapping);
    formData.append('mode', mode);
    const response = await api.post('/dictionaries/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    });
    return response.data;
  },
};

// Case import wizard (eDiscovery intake) API
export const intakeApi = {
  getRequest: async (caseId: string): Promise<RequestInfo> => {
    const response = await api.get(`/cases/${caseId}/request`);
    return response.data;
  },

  /** Requests (open and closed) whose number matches `q` ("24", "24-12", "1249"), newest first. */
  listRequests: async (q = '', limit = 100): Promise<RequestSearchResult> => {
    const response = await api.get('/intake/requests', { params: { q: q || undefined, limit } });
    return response.data;
  },

  getRequestService: async (): Promise<RequestServiceStatus> => {
    const response = await api.get('/intake/request-service');
    return response.data;
  },

  /** One request with its text; with its `number` the server reads just the page that holds it. */
  getExternalRequest: async (requestId: string, number?: string | null): Promise<ExternalRequestDetail> => {
    const response = await api.get(`/intake/requests/${requestId}`, { params: { number: number || undefined } });
    return response.data;
  },

  listEDiscoverySystems: async (): Promise<EDiscoverySystem[]> => {
    const response = await api.get('/intake/ediscovery-systems');
    return response.data.systems;
  },

  /**
   * Suggest the parts of a Purview search for the case's request (R2-12). The
   * internal LLM reads only the request text and the case's dates; rules take
   * over when it's unavailable. `requestText` overrides the case's notes.
   */
  suggestPurviewQuery: async (caseId: string, requestText?: string): Promise<PurviewQuerySuggestion> => {
    const response = await api.post(`/cases/${caseId}/purview-query`, { request_text: requestText }, {
      timeout: 90_000, // one LLM call, capped at 60 s on the server
    });
    return response.data;
  },

  getSources: async (caseId: string): Promise<EDiscoverySource[]> => {
    const response = await api.get(`/cases/${caseId}/ediscovery-sources`);
    return response.data.sources;
  },

  searchEDiscoveryCases: async (system: EDiscoverySystem, query: string): Promise<EDiscoveryCaseSummary[]> => {
    const response = await api.get(`/intake/ediscovery-systems/${system}/cases`, { params: { q: query } });
    return response.data.cases;
  },

  listEDiscoveryExports: async (system: EDiscoverySystem, externalCaseId: string): Promise<EDiscoveryCaseExports> => {
    const response = await api.get(
      `/intake/ediscovery-systems/${system}/cases/${encodeURIComponent(externalCaseId)}/exports`,
    );
    return response.data;
  },

  addSource: async (
    caseId: string,
    system: EDiscoverySystem,
    externalCaseId: string,
    exportIds?: string[],
    options: { autoIngest?: boolean } = {},
  ): Promise<EDiscoverySource> => {
    const response = await api.post(`/cases/${caseId}/ediscovery-sources`, {
      system,
      external_case_id: externalCaseId,
      ...(exportIds ? { export_ids: exportIds } : {}),
      // Ingest once prepared, with no further call (the wizard has navigated away).
      ...(options.autoIngest ? { auto_ingest: true } : {}),
    });
    return response.data;
  },

  /**
   * Run a search in the eDiscovery system and import its export (sc-11767): the
   * server creates the search and starts it; the worker exports it when the
   * estimate finishes and imports the export. A mailbox or site the system
   * doesn't know is a 422 with its message; a run already going is a 409.
   */
  runEDiscoverySearch: async (
    caseId: string,
    run: {
      system: EDiscoverySystem;
      externalCaseId: string;
      query: string;
      scope: EDiscoverySearchScope;
      sources: string[];
    },
  ): Promise<EDiscoverySource> => {
    const response = await api.post(`/cases/${caseId}/ediscovery-sources/search-runs`, {
      system: run.system,
      external_case_id: run.externalCaseId,
      query: run.query,
      scope: run.scope,
      sources: run.sources,
    }, {
      timeout: 120_000, // creates the search and adds each data source, one Graph call each
    });
    return response.data;
  },

  setSelectedFiles: async (
    caseId: string,
    sourceId: string,
    selectedFileIds: string[]
  ): Promise<EDiscoverySource> => {
    const response = await api.patch(`/cases/${caseId}/ediscovery-sources/${sourceId}`, {
      selected_file_ids: selectedFileIds,
    });
    return response.data;
  },

  ingestSource: async (caseId: string, sourceId: string): Promise<EDiscoverySource> => {
    const response = await api.post(`/cases/${caseId}/ediscovery-sources/${sourceId}/ingest`);
    return response.data;
  },

  deleteSource: async (caseId: string, sourceId: string): Promise<void> => {
    await api.delete(`/cases/${caseId}/ediscovery-sources/${sourceId}`);
  },

  getTopEntities: async (caseId: string, limit = 30): Promise<TopEntity[]> => {
    const response = await api.get(`/cases/${caseId}/top-entities`, { params: { limit } });
    return response.data.entities;
  },

  analyzeContext: async (caseId: string): Promise<void> => {
    await api.post(`/cases/${caseId}/analyze-context`);
  },

  getCorrelationRisks: async (caseId: string): Promise<ContextAnalysis> => {
    const response = await api.get(`/cases/${caseId}/correlation-risks`);
    return response.data;
  },

  // Promote a 'general' correlation pattern into a reusable, tenant-global
  // steering hint that future cases' context analysis will look for (C4).
  // Dismiss a correlation risk (or undo it); stored by its stable key, so it
  // stays dismissed after a re-analysis that finds it again (FR-14).
  dismissRisk: async (caseId: string, riskKey: string): Promise<void> => {
    await api.put(`/cases/${caseId}/correlation-risks/${riskKey}/dismissal`);
  },

  undoDismissRisk: async (caseId: string, riskKey: string): Promise<void> => {
    await api.delete(`/cases/${caseId}/correlation-risks/${riskKey}/dismissal`);
  },

  promotePattern: async (caseId: string, pattern: CorrelationRisk): Promise<SteeringHint> => {
    const response = await api.post(`/cases/${caseId}/patterns/promote`, {
      name: pattern.name,
      rationale: pattern.rationale,
      description: pattern.description,
      severity: pattern.severity,
      involved_terms: pattern.involved_terms,
    });
    return response.data;
  },

  listSteeringHints: async (): Promise<SteeringHint[]> => {
    const response = await api.get('/intake/steering-hints');
    return response.data.hints;
  },

  // Stop using a kept risk pattern on future cases (sc-11888).
  deleteSteeringHint: async (hintId: string): Promise<void> => {
    await api.delete(`/intake/steering-hints/${encodeURIComponent(hintId)}`);
  },
};

/**
 * Admin-only: the tenant's connections to external services (NextRequest,
 * Purview). Credential VALUES never travel back from the server — a slot
 * carries its form schema and connection state only, so the UI can never
 * prefill a secret and an update must send only what was re-typed.
 */
export const adminIntegrationsApi = {
  listSlots: async (): Promise<IntegrationSlot[]> => {
    const response = await api.get('/admin/integrations');
    return response.data.slots;
  },

  connect: async (slotKey: string, metadata: Record<string, string>): Promise<IntegrationSlot> => {
    const response = await api.post(`/admin/integrations/${slotKey}`, { metadata });
    return response.data;
  },

  // Omit a field to keep its stored value; sending '' would overwrite it.
  update: async (
    slotKey: string,
    body: { metadata?: Record<string, string>; status?: 'enabled' | 'disabled' },
  ): Promise<IntegrationSlot> => {
    const response = await api.patch(`/admin/integrations/${slotKey}`, body);
    return response.data;
  },

  disconnect: async (slotKey: string): Promise<void> => {
    await api.delete(`/admin/integrations/${slotKey}`);
  },

  // Probes the credential through RedactIt's own provider without storing it.
  test: async (slotKey: string, metadata: Record<string, string>): Promise<IntegrationHealth> => {
    const response = await api.post(`/admin/integrations/${slotKey}/test`, { metadata });
    return response.data;
  },
};

// Generic API helper functions
export const apiHelpers = {
  // Handle API errors consistently
  handleError: (error: unknown): string => {
    const detail = apiHelpers.errorDetail(error);
    if (detail) {
      return detail;
    }
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as { message?: unknown } | undefined;
      if (typeof data?.message === 'string' && data.message.trim()) {
        return data.message;
      }
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  },

  // Extract just the server-supplied `detail`, normalized to a string.
  // Returns undefined when the response carries none, so callers can supply
  // their own fallback copy instead of surfacing "Request failed with status...".
  errorDetail: (error: unknown): string | undefined => {
    if (!axios.isAxiosError(error)) return undefined;
    const data = error.response?.data as { detail?: unknown } | undefined;
    return normalizeErrorDetail(data?.detail);
  },

  // Check if error is a network error
  isNetworkError: (error: unknown): boolean => {
    return axios.isAxiosError(error) && !error.response && !!error.request;
  },

  // HTTP status of a failed request, if it got a response
  statusCode: (error: unknown): number | undefined => {
    return axios.isAxiosError(error) ? error.response?.status : undefined;
  },

  // Check if error is a server error (5xx)
  isServerError: (error: unknown): boolean => {
    return axios.isAxiosError(error) && (error.response?.status ?? 0) >= 500;
  },

  // Check if error is a client error (4xx)
  isClientError: (error: unknown): boolean => {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    return status !== undefined && status >= 400 && status < 500;
  },

  // Check if error is unauthorized (401)
  isUnauthorized: (error: unknown): boolean => {
    return axios.isAxiosError(error) && error.response?.status === 401;
  },

  // Check if error is forbidden (403)
  isForbidden: (error: unknown): boolean => {
    return axios.isAxiosError(error) && error.response?.status === 403;
  },

  // Check if error is not found (404)
  isNotFound: (error: unknown): boolean => {
    return axios.isAxiosError(error) && error.response?.status === 404;
  },
};

// =============================================================================
// PST (Outlook Personal Storage Table) API
// =============================================================================

import type {
  PSTStructure,
  PSTMessageList,
  PSTMessage,
  PSTEntityLocation,
  PSTBatchRedactionResponse,
  PSTExtractionResult,
} from '../types';

// PST API endpoints
export const pstApi = {
  // Get PST file structure (folders and message counts)
  getStructure: async (documentId: string): Promise<PSTStructure> => {
    const response = await api.get(`/pst/${documentId}/structure`);
    return response.data;
  },

  // Get message headers (lazy loading - paginated)
  getMessages: async (
    documentId: string,
    offset: number = 0,
    limit: number = 50
  ): Promise<PSTMessageList> => {
    const response = await api.get(`/pst/${documentId}/messages`, {
      params: { offset, limit },
    });
    return response.data;
  },

  // Get full message content (on-demand loading)
  getMessage: async (documentId: string, messageIndex: number): Promise<PSTMessage> => {
    const response = await api.get(`/pst/${documentId}/messages/${messageIndex}`);
    return response.data;
  },

  // Download attachment from a message
  downloadAttachment: async (
    documentId: string,
    messageIndex: number,
    attachmentIndex: number
  ): Promise<Blob> => {
    const response = await api.get(
      `/pst/${documentId}/messages/${messageIndex}/attachments/${attachmentIndex}`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  // Apply batch redactions and export
  applyBatchRedactions: async (
    documentId: string,
    entities: PSTEntityLocation[]
  ): Promise<PSTBatchRedactionResponse> => {
    const response = await api.post(`/pst/${documentId}/batch-redact`, { entities });
    return response.data;
  },

  // Extract all text for entity detection
  extractText: async (documentId: string): Promise<PSTExtractionResult> => {
    const response = await api.get(`/pst/${documentId}/extract-text`);
    return response.data;
  },

  // Get children completion status for PST container
  getChildrenStatus: async (documentId: string): Promise<{
    total_children: number;
    completed_children: number;
    all_completed: boolean;
    children_status: Array<{ id: string; name: string; status: string }>;
  }> => {
    const response = await api.get(`/pst/${documentId}/children-status`);
    return response.data;
  },

  // Finalize PST container - create ZIP of redacted children
  finalize: async (documentId: string): Promise<{
    document_id: string;
    redacted_path: string;
    children_count: number;
    status: string;
    message: string;
  }> => {
    const response = await api.post(`/pst/${documentId}/finalize`);
    return response.data;
  },
};

export default api;
